import { useEffect } from 'react';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  duration?: number; // ms, default 5000
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const iconPaths = {
  success: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  info: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  warning: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
};

const colorClasses = {
  success: {
    border: 'border-l-green-500',
    bg: 'bg-white dark:bg-gray-800',
    iconColor: 'text-green-500',
    titleColor: 'text-green-900 dark:text-green-400',
    messageColor: 'text-green-700 dark:text-green-300',
  },
  error: {
    border: 'border-l-red-500',
    bg: 'bg-white dark:bg-gray-800',
    iconColor: 'text-red-500',
    titleColor: 'text-red-900 dark:text-red-400',
    messageColor: 'text-red-700 dark:text-red-300',
  },
  info: {
    border: 'border-l-blue-500',
    bg: 'bg-white dark:bg-gray-800',
    iconColor: 'text-blue-500',
    titleColor: 'text-blue-900 dark:text-blue-400',
    messageColor: 'text-blue-700 dark:text-blue-300',
  },
  warning: {
    border: 'border-l-yellow-500',
    bg: 'bg-white dark:bg-gray-800',
    iconColor: 'text-yellow-500',
    titleColor: 'text-yellow-900 dark:text-yellow-400',
    messageColor: 'text-yellow-700 dark:text-yellow-300',
  },
};

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const colors = colorClasses[toast.type];
  const duration = toast.duration ?? 5000;

  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, duration, onDismiss]);

  return (
    <div
      className={`${colors.bg} ${colors.border} pointer-events-auto flex w-full max-w-md overflow-hidden rounded-lg border-l-4 shadow-lg ring-1 ring-black ring-opacity-5 animate-slide-in-right`}
    >
      <div className="flex-1 p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className={`h-6 w-6 ${colors.iconColor}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={iconPaths[toast.type]} />
            </svg>
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className={`text-sm font-medium ${colors.titleColor}`}>{toast.title}</p>
            {toast.message && (
              <p className={`mt-1 text-sm ${colors.messageColor}`}>{toast.message}</p>
            )}
          </div>
          <div className="ml-4 flex flex-shrink-0">
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              className="inline-flex rounded-md text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  // Show max 5 toasts
  const visibleToasts = toasts.slice(0, 5);

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {visibleToasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
